// Chargeability.jsx — blended chargeability by area / function level / month

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Styling
import { chargeabilityCss } from '@digital_monorepo/styling';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** '2026-08' → 'Aug.26' */
const formatMonth = (key) => {
  const [year, month] = key.split('-');
  return `${MONTH_LABELS[Number(month) - 1]}.${year.slice(2)}`;
};

/** Portuguese-style decimal comma, matching the reference report. */
const formatPct = (value) =>
  value === null || value === undefined
    ? '—'
    : `${value.toFixed(1).replace('.', ',')}%`;

const heatClass = (value) => {
  if (value === null || value === undefined) return chargeabilityCss.empty;
  if (value < 20) return chargeabilityCss.heat0;
  if (value < 40) return chargeabilityCss.heat1;
  if (value < 60) return chargeabilityCss.heat2;
  if (value < 80) return chargeabilityCss.heat3;
  return chargeabilityCss.heat4;
};

const summaryStatus = value => {
  if (value === null || value === undefined) return null;
  if (value < 50) return { className: chargeabilityCss.critical, label: 'Below 50%' };
  if (value < 79.5) return { className: chargeabilityCss.warning, label: 'Below 79.5%' };
  return null;
};

const ValueRow = ({ values, monthIndices, compact }) =>
  monthIndices.map(index => {
    const value = values[index];
    const status = compact ? summaryStatus(value) : null;
    return (
      <td key={index} className={`${chargeabilityCss.cell} ${compact ? status?.className || '' : heatClass(value)}`}
        title={status?.label} aria-label={status ? `${formatPct(value)}, ${status.label}` : undefined}>
        {status && <span className={chargeabilityCss.statusIcon} aria-hidden='true'>!</span>}
        {formatPct(value)}
      </td>
    );
  });

const SUMMARY_AREA_ORDER = ['TC', 'TO', 'BC'];

const Chargeability = ({ report, compact = false, endMonth }) => {
  const { t } = useTranslation();

  const months = report?.months || [];
  const areas = report?.areas || [];
  const monthIndices = months.flatMap((month, index) => (!endMonth || month <= endMonth ? [index] : []));
  const displayedAreas = compact
    ? [...areas].sort((a, b) => SUMMARY_AREA_ORDER.indexOf(a.key) - SUMMARY_AREA_ORDER.indexOf(b.key))
    : areas;

  const monthHeaders = monthIndices.map(index => formatMonth(months[index]));
  const wrapperClass = `${chargeabilityCss.tableWrapper} ${compact ? chargeabilityCss.compactWrapper : ''}`;

  if (!monthIndices.length || !areas.length) {
    return (
      <div className={wrapperClass}>
        <div className={chargeabilityCss.stateBox}>
          {t('CHARGEABILITY.EMPTY')}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={wrapperClass}>
        <div className={chargeabilityCss.tableContainer}>
          <table className={chargeabilityCss.table}>
            <thead>
              <tr>
                <th className={`${chargeabilityCss.th} ${chargeabilityCss.thLabel}`}>
                  {compact ? t('PROFESSIONALS_TABLE.HEADERS.AREA') : t('CHARGEABILITY.HEADERS.GROUP')}
                </th>
                <th className={`${chargeabilityCss.th} ${chargeabilityCss.thHeads}`}>
                  {t('CHARGEABILITY.HEADERS.HEADCOUNT')}
                </th>
                {monthHeaders.map((label) => (
                  <th key={label} className={chargeabilityCss.th}>{label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {displayedAreas.map((area) => (
                <React.Fragment key={area.key}>
                  <tr className={chargeabilityCss.areaRow}>
                    <td className={chargeabilityCss.labelCell}>
                      <span className={chargeabilityCss.areaLabel}>
                        <span className={chargeabilityCss.areaBadge}>{area.key}</span>
                        {area.label}
                      </span>
                    </td>
                    <td className={chargeabilityCss.headsCell}>{area.headcount}</td>
                    <ValueRow values={area.values} monthIndices={monthIndices} compact={compact} />
                  </tr>

                  {!compact && area.levels.map((level) => (
                    <tr key={`${area.key}-${level.key}`} className={chargeabilityCss.levelRow}>
                      <td className={`${chargeabilityCss.labelCell} ${chargeabilityCss.levelLabel}`}>
                        {level.label}
                      </td>
                      <td className={chargeabilityCss.headsCell}>{level.headcount}</td>
                      <ValueRow values={level.values} monthIndices={monthIndices} compact={compact} />
                    </tr>
                  ))}
                </React.Fragment>
              ))}

              {report.total && (
                <tr className={chargeabilityCss.totalRow}>
                  <td className={chargeabilityCss.labelCell}>
                    {t('CHARGEABILITY.TOTAL')}
                  </td>
                  <td className={chargeabilityCss.headsCell}>{report.total.headcount}</td>
                  <ValueRow values={report.total.values} monthIndices={monthIndices} compact={compact} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

Chargeability.propTypes = {
  report: PropTypes.shape({
    months: PropTypes.arrayOf(PropTypes.string),
    areas:  PropTypes.array,
    total:  PropTypes.object,
    unmapped: PropTypes.array,
  }),
  compact: PropTypes.bool,
  endMonth: PropTypes.string,
};

export default Chargeability;
